import { Building2, ExternalLink, Plus, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from 'tailwind-v4/components/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'tailwind-v4/components/empty'
import { CreateOrganisationDialog } from './create-organisation-dialog'

const SUPPORT_URL = 'https://stampede.ai/support'

function ContactSupportButton({ variant }: { variant: 'primary' | 'outline' }) {
  return (
    <Button
      variant={variant}
      size="lg"
      className="w-full sm:w-auto"
      rightIcon={<ExternalLink />}
      render={
        <a
          href={SUPPORT_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="Contact support"
        />
      }
    >
      Contact support
    </Button>
  )
}

function CheckAgainButton({
  onRefresh,
  isRefreshing,
}: {
  onRefresh: () => void
  isRefreshing: boolean
}) {
  return (
    <Button
      variant="outline"
      size="lg"
      className="w-full sm:w-auto"
      leftIcon={<RefreshCw />}
      disabled={isRefreshing}
      onClick={onRefresh}
    >
      {isRefreshing ? 'Checking…' : 'Check again'}
    </Button>
  )
}

/**
 * Shown on `/` when the signed-in account belongs to zero organisations and has
 * no pending invitation — a brand-new signup. The primary route forward is
 * self-serve creation of your own organisation (30 days free trial).
 */
export function NoOrganisations({
  onRefresh,
  isRefreshing,
}: {
  onRefresh: () => void
  isRefreshing: boolean
}) {
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <Empty className="flex-none justify-start">
      <EmptyHeader className="max-w-md">
        <EmptyMedia variant="icon">
          <Building2 />
        </EmptyMedia>
        <EmptyTitle>Start your free trial</EmptyTitle>
        <EmptyDescription>
          New to Stampede? Get your own organisation running in a couple of
          minutes — 30 days free, full access.
        </EmptyDescription>
      </EmptyHeader>

      <EmptyContent className="gap-6">
        <Button
          variant="primary"
          size="lg"
          className="w-full sm:w-auto"
          leftIcon={<Plus />}
          onClick={() => {
            setCreateOpen(true)
          }}
        >
          Start free trial
        </Button>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <CheckAgainButton onRefresh={onRefresh} isRefreshing={isRefreshing} />
          <ContactSupportButton variant="outline" />
        </div>
      </EmptyContent>

      <CreateOrganisationDialog open={createOpen} onOpenChange={setCreateOpen} />

      {/* Escape hatch for someone who signed in with the wrong address. */}
      <div className="flex max-w-sm flex-wrap items-center justify-center gap-1 text-xs text-muted-foreground">
        <span>Signed in with the wrong address?</span>
        <Button
          variant="link"
          size="lg"
          className="text-xs"
          onClick={() => navigate('oauth/logout')}
        >
          Use a different account
        </Button>
      </div>
    </Empty>
  )
}

export default NoOrganisations
